import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Package, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { updateWashingProgramProducts } from '../Services/washingPrograms.services';
import { fetchProducts } from '@/pages/parametrizacion/products/Services/products.services';

const ProgramProductsModal = ({ isOpen, onClose, program, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && program) {
      loadData();
    }
  }, [isOpen, program]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load available products
      const response = await fetchProducts({ limit: 500, offset: 1, query: { estado: 'Activo' } });
      const allProducts = response?.data || [];
      setAvailableProducts(allProducts);

      // Load existing products from program
      const existingProducts = program?.productos || [];
      setProducts(existingProducts.map(p => ({
        producto: p.producto,
        cantidadMililitros: p.cantidadMililitros || 0,
      })));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los productos.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProduct = (product) => {
    if (products.some(p => p.producto.id === product._id)) {
      toast({
        title: 'Producto ya agregado',
        description: 'Este producto ya está en la lista.',
        variant: 'destructive',
      });
      return;
    }

    setProducts(prev => [...prev, {
      producto: {
        id: product._id,
        nombre: product.nombre,
      },
      cantidadMililitros: 0,
    }]);
  };

  const handleRemoveProduct = (index) => {
    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    setProducts(prev => prev.map((p, i) => 
      i === index ? { ...p, cantidadMililitros: numValue } : p
    ));
  };

  const handleSave = async () => {
    if (!program?._id) return;
    
    setIsSaving(true);
    try {
      await updateWashingProgramProducts(program._id, products);
      toast({
        title: 'Éxito',
        description: 'Productos del programa actualizados correctamente.',
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron guardar los productos.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredAvailable = availableProducts.filter(p => 
    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !products.some(ep => ep.producto.id === p._id)
  );

  const totalMililitros = products.reduce((sum, p) => sum + (p.cantidadMililitros || 0), 0);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b bg-gray-50">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Productos del Programa
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {program?.nombre} - #{program?.numero_programa}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[60vh]">
              {isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {/* Products List */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">
                        Productos Asignados ({products.length})
                      </h3>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        Total: {totalMililitros.toLocaleString()} ml
                      </Badge>
                    </div>

                    {products.length === 0 ? (
                      <Card className="border-dashed bg-gray-50">
                        <CardContent className="py-6 text-center text-gray-400">
                          <Package className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">No hay productos asignados</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="space-y-2">
                        {products.map((item, index) => (
                          <motion.div
                            key={item.producto.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {item.producto.nombre}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">ml:</span>
                              <Input
                                type="number"
                                value={item.cantidadMililitros || ''}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                className="w-24 h-8 text-sm"
                                placeholder="0"
                                min="0"
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveProduct(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Products */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Agregar Productos
                    </h3>
                    <div className="relative mb-3">
                      <Input
                        type="text"
                        placeholder="Buscar producto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                      <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>

                    <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
                      {filteredAvailable.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">
                          {searchTerm ? 'No se encontraron productos' : 'Escribe para buscar productos'}
                        </p>
                      ) : (
                        filteredAvailable.map((product) => (
                          <button
                            key={product._id}
                            onClick={() => handleAddProduct(product)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-md hover:bg-blue-50 transition-colors group"
                          >
                            <Plus className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                            <span className="text-sm text-gray-700 group-hover:text-blue-700 flex-1">
                              {product.nombre}
                            </span>
                            <span className="text-xs text-gray-400">
                              {product.unidad}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                {products.length} producto(s) • {totalMililitros.toLocaleString()} ml total
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={isSaving}
                  className="gap-2"
                >
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Guardar Productos
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProgramProductsModal;
